import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getEmployees, createEmployee, deleteEmployee, getCountries, getManagersLookup } from '../utils/api';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  UserGroupIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const Employees = () => {
  usePageTitle('Employees');

  const navigate = useNavigate();
  const { canEditEmployees, canDeleteEmployees } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [countries, setCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_email: '',
    personal_email: '',
    phone: '',
    date_of_birth: '',
    home_address: '',
    country: '',
    position: '',
    manager_id: '',
    employment_type: 'employment_contract',
    current_start_date: '',
    current_end_date: ''
  });

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isActive, employmentType, pagination.page]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Failed to load countries:', error);
      }
    };
    loadCountries();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {
        search,
        page: pagination.page,
        limit: pagination.limit,
        ...(isActive && { is_active: isActive }),
        ...(employmentType && { employment_type: employmentType })
      };
      const response = await getEmployees(params);
      setEmployees(response.data.employees);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch employees');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setFormData({
      first_name: '',
      last_name: '',
      company_email: '',
      personal_email: '',
      phone: '',
      date_of_birth: '',
      home_address: '',
      country: '',
      position: '',
      manager_id: '',
      employment_type: 'employment_contract',
      current_start_date: '',
      current_end_date: ''
    });
    setManagerSearch('');
    fetchManagers('');
    setModalOpen(true);
  };

  const fetchManagers = async (search) => {
    try {
      const response = await getManagersLookup({ search });
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Clean up formData - remove empty strings for optional fields
      const cleanData = { ...formData };
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') {
          delete cleanData[key];
        }
      });

      console.log('Submitting employee data:', JSON.stringify(cleanData, null, 2));
      await createEmployee(cleanData);
      toast.success('Employee created successfully');
      closeModal();
      fetchEmployees();
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response data:', JSON.stringify(error.response?.data, null, 2));

      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMsg = error.response.data.errors.map(err => `${err.param}: ${err.msg}`).join(', ');
        toast.error(errorMsg);
      } else {
        toast.error(error.response?.data?.error || 'Failed to create employee');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const response = await deleteEmployee(id);
      if (response.data.soft_delete) {
        toast.success('Employee deactivated (has associated records)');
      } else {
        toast.success('Employee deleted successfully');
      }
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
      console.error(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading && employees.length === 0) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading employees...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <UserGroupIcon style={{ width: 28, height: 28, marginRight: 8 }} />
            Employees
          </h1>
          <p className="page-subtitle">Manage employee records and employment history</p>
        </div>
        {canEditEmployees && (
          <button className="btn btn-primary" onClick={openModal}>
            <PlusIcon style={{ width: 18, height: 18 }} />
            Add Employee
          </button>
        )}
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              placeholder="Search by name, email, or employee ID..."
              value={search}
              onChange={handleSearch}
              className="form-input search-input"
            />
          </div>
          <button
            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon style={{ width: 18, height: 18 }} />
            Filters
          </button>
        </div>

        {showFilters && (
          <div style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="filters-row">
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  className="form-select"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Employment Type</label>
                <select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  className="form-select"
                >
                  <option value="">All Types</option>
                  <option value="employment_contract">Employment Contract</option>
                  <option value="vendor_contract">Vendor Contract</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Company Email</th>
                <th>Country</th>
                <th>Employment Type</th>
                <th>Start Date</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-tertiary)' }}>
                    No employees found
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <tr key={employee.id}>
                    <td>
                      <span className="mono" style={{ color: 'var(--color-accent-info)' }}>
                        {employee.employee_id}
                      </span>
                    </td>
                    <td>
                      <strong>{employee.first_name} {employee.last_name}</strong>
                    </td>
                    <td style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                      {employee.company_email}
                    </td>
                    <td>{employee.country || '—'}</td>
                    <td>
                      <span className={`badge ${employee.employment_type === 'employment_contract' ? 'badge-approved' : 'badge-draft'}`}>
                        {employee.employment_type === 'employment_contract' ? 'Employment' : 'Vendor'}
                      </span>
                    </td>
                    <td>
                      {employee.current_start_date ? new Date(employee.current_start_date).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <span className={`badge ${employee.is_active ? 'badge-paid' : 'badge-unpaid'}`}>
                        {employee.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => navigate(`/employees/${employee.id}`)}
                          title="View Details"
                        >
                          <EyeIcon style={{ width: 16, height: 16 }} />
                        </button>
                        {canDeleteEmployees && (
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleDelete(employee.id)}
                            title="Delete"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <button
              className="btn btn-sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create Employee Modal */}
      {modalOpen && (
        <Modal
          isOpen={true}
          title="Add New Employee"
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  className="form-input"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  className="form-input"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company Email *</label>
                <input
                  type="email"
                  name="company_email"
                  className="form-input"
                  value={formData.company_email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Personal Email</label>
                <input
                  type="email"
                  name="personal_email"
                  className="form-input"
                  value={formData.personal_email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="form-input"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                name="country"
                className="form-select"
                value={formData.country}
                onChange={handleInputChange}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Home Address</label>
              <textarea
                name="home_address"
                className="form-textarea"
                value={formData.home_address}
                onChange={handleInputChange}
                rows="2"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Position</label>
                <input
                  type="text"
                  name="position"
                  className="form-input"
                  value={formData.position}
                  onChange={handleInputChange}
                  maxLength={100}
                  placeholder="e.g., Software Engineer"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Manager</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search for manager..."
                    value={managerSearch}
                    onChange={(e) => {
                      setManagerSearch(e.target.value);
                      fetchManagers(e.target.value);
                    }}
                  />
                  {managers.length > 0 && managerSearch && (
                    <div className="dropdown-list" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      zIndex: 100
                    }}>
                      {managers.map((m) => (
                        <div
                          key={m.id}
                          className="dropdown-item"
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--color-border)'
                          }}
                          onClick={() => {
                            setFormData({ ...formData, manager_id: m.id });
                            setManagerSearch(`${m.first_name} ${m.last_name} (${m.employee_id})`);
                            setManagers([]);
                          }}
                        >
                          <strong>{m.first_name} {m.last_name}</strong>
                          <span style={{ color: 'var(--color-text-secondary)', marginLeft: '8px' }}>
                            {m.employee_id} {m.position && `• ${m.position}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(formData.manager_id || managerSearch) && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: '8px', color: 'var(--color-accent-danger)' }}
                    onClick={() => {
                      setFormData({ ...formData, manager_id: '' });
                      setManagerSearch('');
                      setManagers([]);
                    }}
                  >
                    Remove Manager
                  </button>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Employment Type *</label>
                <select
                  name="employment_type"
                  className="form-select"
                  value={formData.employment_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="employment_contract">Employment Contract</option>
                  <option value="vendor_contract">Vendor Contract</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input
                  type="date"
                  name="current_start_date"
                  className="form-input"
                  value={formData.current_start_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">End Date (Optional)</label>
              <input
                type="date"
                name="current_end_date"
                className="form-input"
                value={formData.current_end_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="info-box" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                <strong>Note:</strong> A user account will be automatically created with username = company email and password = "changeme123"
              </p>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creating...' : 'Create Employee'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Employees;
